import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { FiX, FiSearch, FiUsers, FiFilter, FiUpload, FiZoomIn, FiZoomOut, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { useApp } from '../../context/AppContext';

const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$isMobile',
})`
  position: fixed !important;
  top: 0 !important;
  left: ${(p) => (p.$isMobile ? '0' : '320px')} !important;
  right: 0 !important;
  bottom: 0 !important;
  width: ${(p) => (p.$isMobile ? '100vw' : 'calc(100vw - 320px)')} !important;
  height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;
  background: rgba(0, 0, 0, 0.45) !important;
  display: flex !important;
  align-items: stretch !important;
  justify-content: flex-start !important;
  z-index: ${(p) => (p.$isMobile ? '1000080' : '999999')} !important;
  box-sizing: border-box !important;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  min-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => (props.$compact ? '10px 12px' : '20px 32px')};
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
  z-index: 1000;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: ${props => (props.$compact ? '1.05rem' : '1.8rem')};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? '8px' : '12px')};
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.25);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: ${props => (props.$compact ? '10px' : '12px')};
  width: ${props => (props.$compact ? '40px' : '48px')};
  height: ${props => (props.$compact ? '40px' : '48px')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: ${props => (props.$compact ? '20px' : '24px')};
  z-index: 1001;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: rgba(255, 255, 255, 0.4);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? '8px' : '12px')};
  padding: ${props => (props.$compact ? '8px 10px' : '16px 24px')};
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: ${props => (props.$compact ? '0' : '200px')};
  ${props => props.$compact && 'flex-basis: 100%;'}
  padding: ${props => (props.$compact ? '8px 12px' : '10px 16px')};
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: ${props => (props.$compact ? '0.85rem' : '0.95rem')};
  outline: none;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const FilterButton = styled.button`
  padding: ${props => (props.$compact ? '6px 10px' : '10px 16px')};
  border-radius: 8px;
  border: 1px solid ${props => props.$active ? '#667eea' : '#d1d5db'};
  background: ${props => props.$active ? '#667eea' : '#ffffff'};
  color: ${props => props.$active ? '#ffffff' : '#374151'};
  cursor: pointer;
  font-size: ${props => (props.$compact ? '0.72rem' : '0.9rem')};
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? '4px' : '6px')};
  
  &:hover {
    border-color: #667eea;
    background: ${props => props.$active ? '#667eea' : '#f3f4f6'};
  }
`;

const UploadButton = styled.button`
  padding: ${props => (props.$compact ? '7px 12px' : '10px 20px')};
  border-radius: 8px;
  border: 1px solid #10b981;
  background: #10b981;
  color: #ffffff;
  cursor: pointer;
  font-size: ${props => (props.$compact ? '0.75rem' : '0.9rem')};
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${props => (props.$compact ? '4px' : '6px')};
  
  &:hover {
    background: #059669;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomControls = styled.div`
  display: flex;
  gap: ${props => (props.$compact ? '4px' : '8px')};
  align-items: center;
  padding: ${props => (props.$compact ? '5px 6px' : '8px')};
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #d1d5db;
`;

const ZoomButton = styled.button`
  width: ${props => (props.$compact ? '28px' : '32px')};
  height: ${props => (props.$compact ? '28px' : '32px')};
  border: none;
  border-radius: 6px;
  background: #f3f4f6;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #f9fafb;
  min-height: 0;
  max-height: 100%;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: auto;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  max-width: 100%;
  max-height: 100%;
`;

const ChartContent = styled.div`
  position: relative;
  min-width: 100%;
  min-height: 100%;
  padding: ${props => (props.$compact ? '16px 12px' : '60px')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  transform: ${props => `scale(${props.$scale}) translate(${props.$translateX}px, ${props.$translateY}px)`};
  transform-origin: 0 0;
  transition: ${props => props.$isDragging ? 'none' : 'transform 0.1s ease-out'};
  gap: 0;
`;

const LevelContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: ${props => (props.$compact ? '12px' : '40px')};
  margin-bottom: ${props => (props.$compact ? '36px' : '80px')};
  margin-top: ${props => {
    if (props.$isFirst) return '0';
    return props.$compact ? '18px' : '40px';
  }};
  position: relative;
  min-height: ${props => (props.$compact ? '120px' : '200px')};
  width: 100%;
  z-index: 2;
  flex-shrink: 0;
  border-top: ${props => props.$isFirst ? 'none' : '1px dashed rgba(107, 114, 128, 0.2)'};
  padding-top: ${props => {
    if (props.$isFirst) return '0';
    return props.$compact ? '14px' : '40px';
  }};
`;

const EmployeeCard = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: ${props => (props.$compact ? '10px' : '12px')};
  padding: ${props => (props.$compact ? '8px 10px' : '16px')};
  min-width: ${props => (props.$compact ? '132px' : '200px')};
  max-width: ${props => (props.$compact ? '160px' : 'none')};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid ${props => props.$highlighted ? '#667eea' : 'transparent'};
  z-index: 2;
  
  &:hover {
    transform: ${props => (props.$compact ? 'translateY(-2px)' : 'translateY(-4px)')};
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    border-color: #667eea;
  }
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${props => (props.$compact ? '6px' : '12px')};
`;

const Avatar = styled.div`
  width: ${props => (props.$compact ? '44px' : '64px')};
  height: ${props => (props.$compact ? '44px' : '64px')};
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: ${props => (props.$compact ? '15px' : '24px')};
  font-weight: 700;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EmployeeName = styled.div`
  font-weight: 700;
  color: #111827;
  font-size: ${props => (props.$compact ? '0.78rem' : '1rem')};
  text-align: center;
  margin-bottom: ${props => (props.$compact ? '4px' : '6px')};
  line-height: 1.25;
  word-break: break-word;
`;

const EmployeePosition = styled.div`
  font-size: ${props => (props.$compact ? '0.68rem' : '0.85rem')};
  color: #6b7280;
  text-align: center;
  margin-bottom: ${props => (props.$compact ? '4px' : '8px')};
  line-height: 1.25;
`;

const EmployeeDepartment = styled.div`
  font-size: ${props => (props.$compact ? '0.65rem' : '0.75rem')};
  color: #9ca3af;
  text-align: center;
  margin-bottom: ${props => (props.$compact ? '4px' : '8px')};
  line-height: 1.2;
`;

const TeamBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fef3c7;
  color: #92400e;
  font-size: ${props => (props.$compact ? '0.65rem' : '0.75rem')};
  font-weight: 600;
  padding: ${props => (props.$compact ? '2px 6px' : '4px 8px')};
  border-radius: ${props => (props.$compact ? '8px' : '12px')};
  margin-top: ${props => (props.$compact ? '4px' : '8px')};
`;

const LinesOverlay = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 1;
  overflow: visible;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => (props.$compact ? '28px 14px' : '60px 20px')};
  color: #6b7280;
  
  svg {
    font-size: ${props => (props.$compact ? '2.5rem' : '4rem')};
    margin-bottom: ${props => (props.$compact ? '10px' : '16px')};
    opacity: 0.3;
  }
  
  h3 {
    margin: 0 0 8px 0;
    font-size: ${props => (props.$compact ? '1rem' : '1.2rem')};
    color: #374151;
  }
  
  p {
    margin: 0;
    font-size: ${props => (props.$compact ? '0.8rem' : '0.9rem')};
    line-height: ${props => (props.$compact ? '1.4' : 'normal')};
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => (props.$compact ? '28px 14px' : '60px 20px')};
  color: #6b7280;
  font-size: ${props => (props.$compact ? '0.9rem' : 'inherit')};
`;

const FileInput = styled.input`
  display: none;
`;

const EmployeeDetailsOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000000;
  padding: ${props => (props.$compact ? '12px' : '20px')};
`;

const EmployeeDetailsContainer = styled.div`
  background: #ffffff;
  border-radius: ${props => (props.$compact ? '12px' : '16px')};
  width: ${props => (props.$compact ? '100%' : '90%')};
  max-width: ${props => (props.$compact ? '100%' : '700px')};
  max-height: ${props => (props.$compact ? '92vh' : '90vh')};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EmployeeDetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => (props.$compact ? '10px 12px' : '20px 24px')};
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const EmployeeDetailsTitle = styled.h3`
  margin: 0;
  color: #ffffff;
  font-size: ${props => (props.$compact ? '0.95rem' : '1.3rem')};
  font-weight: 700;
  line-height: 1.25;
`;

const EmployeeDetailsBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => (props.$compact ? '14px' : '24px')};
`;

const EmployeeDetailsAvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${props => (props.$compact ? '14px' : '24px')};
  padding-bottom: ${props => (props.$compact ? '14px' : '24px')};
  border-bottom: 1px solid #e5e7eb;
`;

const EmployeeDetailsAvatar = styled.div`
  width: ${props => (props.$compact ? '80px' : '120px')};
  height: ${props => (props.$compact ? '80px' : '120px')};
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: ${props => (props.$compact ? '28px' : '48px')};
  font-weight: 700;
  overflow: hidden;
  margin-bottom: ${props => (props.$compact ? '10px' : '16px')};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EmployeeDetailsFullName = styled.h2`
  margin: 0;
  font-size: ${props => (props.$compact ? '1.1rem' : '1.5rem')};
  font-weight: 700;
  color: #111827;
  text-align: center;
  line-height: 1.25;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: #111827;
  font-weight: 500;
`;

const EmployeeDetailsInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compact ? '12px' : '16px')};

  ${props => props.$compact && `
    ${InfoRow} {
      gap: 4px;
    }
    ${InfoLabel} {
      font-size: 0.7rem;
    }
    ${InfoValue} {
      font-size: 0.88rem;
      line-height: 1.35;
      word-break: break-word;
    }
  `}
`;

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compact ? '8px' : '12px')};
  margin-top: 8px;
`;

const TeamMember = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => (props.$compact ? '8px 10px' : '12px')};
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  
  div:first-child,
  span:first-of-type {
    width: ${props => (props.$compact ? '32px' : '40px')};
    height: ${props => (props.$compact ? '32px' : '40px')};
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: ${props => (props.$compact ? '12px' : '16px')};
    font-weight: 700;
    flex-shrink: 0;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }
  }
`;

// Функция нормализации URL аватарки
const normalizeAvatarUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  let trimmed = url.trim();
  trimmed = trimmed.replace(/\\/g, '/');
  
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  
  const idx = trimmed.toLowerCase().indexOf('/uploads/avatars/');
  if (idx !== -1) {
    return trimmed.slice(idx);
  }
  
  if (!trimmed) return null;
  if (trimmed.startsWith('/uploads') || trimmed.startsWith('/images')) {
    return trimmed;
  }
  if (trimmed.startsWith('uploads/')) {
    return '/' + trimmed;
  }
  return '/uploads/avatars/' + trimmed.replace(/^\/+/, '');
};

// Функция получения инициалов
const getInitials = (firstName, lastName) => {
  const first = (firstName || '').charAt(0).toUpperCase();
  const last = (lastName || '').charAt(0).toUpperCase();
  return (first + last) || '?';
};

// Функция подсчета всех подчиненных
const countAllSubordinates = (node) => {
  if (!node.children || node.children.length === 0) return 0;
  let count = node.children.length;
  node.children.forEach(child => {
    count += countAllSubordinates(child);
  });
  return count;
};

export default function CompanyStructureModal({ open, onClose, isMobile = false }) {
  const { state } = useApp();
  const [tree, setTree] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lineData, setLineData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const chartWrapperRef = useRef(null);
  const chartContentRef = useRef(null);
  const cardRefs = useRef({});

  useEffect(() => {
    if (open) {
      loadStructure();
      loadUsers();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isMobile) {
      setScale(0.82);
    } else {
      setScale(1);
    }
    setTranslateX(0);
    setTranslateY(0);
  }, [open, isMobile]);

  // Слушаем события обновления структуры компании
  useEffect(() => {
    const handleStructureUpdate = () => {
      console.log('🔄 [CompanyStructureModal] Получено событие обновления структуры, перезагружаем данные...');
      if (open) {
        loadStructure();
        loadUsers();
      }
    };

    window.addEventListener('companyStructureUpdated', handleStructureUpdate);

    return () => {
      window.removeEventListener('companyStructureUpdated', handleStructureUpdate);
    };
  }, [open]);

  // Обработчик ESC для закрытия модалки
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (selectedEmployee) {
          setSelectedEmployee(null);
        } else if (open) {
          onClose();
        }
      }
    };
    
    if (open || selectedEmployee) {
      document.addEventListener('keydown', handleEscape);
      // Предотвращаем скролл body когда модалка открыта
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (!open && !selectedEmployee) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [open, onClose, selectedEmployee]);

  // Пересчитываем линии после изменения структуры или размеров
  useEffect(() => {
    if (tree.length > 0) {
      // Задержка для того, чтобы DOM успел отрендериться
      const timer = setTimeout(() => {
        if (chartContentRef.current) {
          const filteredTree = filterTree(tree, searchQuery, filterDepartment);
          const levels = organizeByLevels(filteredTree);
          const lines = collectLineData(levels);
          setLineData(lines);
        }
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setLineData([]);
    }
  }, [tree, searchQuery, filterDepartment, scale, translateX, translateY]);

  // Пересчитываем линии при изменении размера окна
  useEffect(() => {
    const handleResize = () => {
      if (tree.length > 0 && chartContentRef.current) {
        const filteredTree = filterTree(tree, searchQuery, filterDepartment);
        const levels = organizeByLevels(filteredTree);
        const lines = collectLineData(levels);
        setLineData(lines);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tree, searchQuery, filterDepartment]);

  // Отладочная информация о уровнях
  useEffect(() => {
    if (tree.length > 0) {
      const filteredTree = filterTree(tree, searchQuery, filterDepartment);
      console.log('🔍 [DEBUG] Фильтрованное дерево:', {
        total: filteredTree.length,
        nodes: filteredTree.map(n => ({
          id: n.id,
          name: `${n.first_name} ${n.last_name}`,
          manager_id: n.manager_id,
          childrenCount: n.children ? n.children.length : 0
        }))
      });
      
      const levels = organizeByLevels(filteredTree);
      console.log('📊 [DEBUG] Всего уровней:', levels.length);
      console.log('📊 [DEBUG] Уровни структуры:', levels.map((level, idx) => ({
        level: idx,
        count: level.length,
        names: level.map(n => `${n.first_name || ''} ${n.last_name || ''}`.trim()),
        hasChildren: level.map(n => n.children ? n.children.length : 0),
        managerIds: level.map(n => n.manager_id)
      })));
      
      if (levels.length === 1 && levels[0].length > 0) {
        console.warn('⚠️ [WARNING] Все сотрудники на одном уровне! Проверьте поле manager_id в базе данных.');
        console.log('📋 [DEBUG] Все сотрудники:', filteredTree.map(n => ({
          id: n.id,
          name: `${n.first_name} ${n.last_name}`,
          manager_id: n.manager_id,
          manager_name: n.manager_name
        })));
      }
    }
  }, [tree, searchQuery, filterDepartment]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    }
  };

  const loadStructure = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/company-structure/tree', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Ошибка загрузки структуры');
      }
      
      const data = await response.json();
      console.log('📥 [loadStructure] Получены данные с сервера:', {
        total: Array.isArray(data) ? data.length : 0,
        firstNode: data && data[0] ? {
          id: data[0].id,
          name: `${data[0].first_name} ${data[0].last_name}`,
          manager_id: data[0].manager_id,
          childrenCount: data[0].children ? data[0].children.length : 0
        } : null,
        allNodes: Array.isArray(data) ? data.map(n => ({
          id: n.id,
          name: `${n.first_name} ${n.last_name}`,
          manager_id: n.manager_id,
          childrenCount: n.children ? n.children.length : 0
        })) : []
      });
      setTree(data || []);
      
      // Собираем уникальные департаменты
      const depts = new Set();
      const collectDepartments = (nodes) => {
        nodes.forEach(node => {
          if (node.department) {
            depts.add(node.department);
          }
          if (node.children && node.children.length > 0) {
            collectDepartments(node.children);
          }
        });
      };
      collectDepartments(data || []);
      setDepartments(Array.from(depts).sort());
    } catch (error) {
      console.error('Ошибка загрузки структуры компании:', error);
    } finally {
      setLoading(false);
    }
  };

  // Поиск аватара пользователя по имени
  const findUserAvatar = (firstName, lastName, node = null) => {
    // Сначала проверяем аватарку в company_structure (если есть node)
    if (node && node.avatar_url) {
      return normalizeAvatarUrl(node.avatar_url);
    }
    
    // Затем проверяем в таблице users
    const fullName = `${firstName} ${lastName}`.trim();
    const user = users.find(u => {
      const userName = (u.username || '').trim();
      return userName === fullName || userName.toLowerCase() === fullName.toLowerCase();
    });
    return user?.avatar ? normalizeAvatarUrl(user.avatar) : null;
  };

  const matchesSearch = (node, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    const fullName = `${node.first_name || ''} ${node.last_name || ''}`.toLowerCase();
    const position = (node.position || '').toLowerCase();
    const department = (node.department || '').toLowerCase();
    const subdivision = (node.subdivision || '').toLowerCase();
    
    return fullName.includes(q) || position.includes(q) || department.includes(q) || subdivision.includes(q);
  };

  const matchesFilter = (node, filter) => {
    if (filter === 'all') return true;
    return node.department === filter;
  };

  const filterTree = (nodes, query, filter) => {
    return nodes.filter(node => {
      const nodeMatches = matchesSearch(node, query) && matchesFilter(node, filter);
      const hasMatchingChildren = node.children && node.children.length > 0 && 
        filterTree(node.children, query, filter).length > 0;
      return nodeMatches || hasMatchingChildren;
    }).map(node => {
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: filterTree(node.children, query, filter)
        };
      }
      return node;
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      alert('Пожалуйста, выберите файл Excel (.xlsx или .xls)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/company-structure/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка импорта данных');
      }
      
      const result = await response.json();
      alert(`Успешно импортировано ${result.inserted} сотрудников`);
      
      await loadStructure();
    } catch (error) {
      console.error('Ошибка обработки файла:', error);
      alert(`Ошибка импорта: ${error.message}`);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Организация дерева по уровням
  const organizeByLevels = (nodes) => {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      console.warn('⚠️ [organizeByLevels] Пустой или невалидный массив узлов');
      return [];
    }
    
    const levels = [];
    const processed = new Set();
    
    console.log('🔄 [organizeByLevels] Начинаем организацию, узлов:', nodes.length);
    
    const processNode = (node, level) => {
      if (!node || !node.id) {
        console.warn('⚠️ [processNode] Невалидный узел:', node);
        return;
      }
      
      if (processed.has(node.id)) {
        console.log(`⏭️ [processNode] Узел ${node.id} уже обработан, пропускаем`);
        return;
      }
      
      if (!levels[level]) {
        levels[level] = [];
      }
      
      levels[level].push(node);
      processed.add(node.id);
      
      console.log(`✅ [processNode] Узел ${node.id} (${node.first_name} ${node.last_name}) добавлен на уровень ${level}, детей: ${node.children ? node.children.length : 0}`);
      
      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        node.children.forEach(child => {
          if (child && child.id && !processed.has(child.id)) {
            processNode(child, level + 1);
          }
        });
      }
    };
    
    nodes.forEach((node, idx) => {
      if (!processed.has(node.id)) {
        console.log(`🌳 [organizeByLevels] Обрабатываем корневой узел ${idx + 1}/${nodes.length}:`, node.id, `${node.first_name} ${node.last_name}`);
        processNode(node, 0);
      }
    });
    
    const result = levels.filter(level => level && Array.isArray(level) && level.length > 0);
    console.log(`📊 [organizeByLevels] Результат: ${result.length} уровней, обработано узлов: ${processed.size}`);
    return result;
  };

  // Обработка масштабирования
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleResetZoom = () => {
    setScale(isMobile ? 0.82 : 1);
    setTranslateX(0);
    setTranslateY(0);
  };

  // Обработка перетаскивания
  const handleMouseDown = (e) => {
    if (e.button === 0) { // Левая кнопка мыши
      setIsDragging(true);
      setDragStart({
        x: e.clientX - translateX,
        y: e.clientY - translateY
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setTranslateX(e.clientX - dragStart.x);
      setTranslateY(e.clientY - dragStart.y);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // Рендеринг карточки сотрудника
  const renderEmployeeCard = (node, level, cardRef) => {
    const fullName = `${node.first_name || ''} ${node.last_name || ''}`.trim();
    const highlighted = searchQuery && matchesSearch(node, searchQuery);
    const avatarUrl = findUserAvatar(node.first_name, node.last_name);
    const initials = getInitials(node.first_name, node.last_name);
    const directReports = node.children ? node.children.length : 0;
    const totalTeam = countAllSubordinates(node);

    return (
      <EmployeeCard
        key={node.id}
        ref={cardRef}
        data-node-id={node.id}
        $highlighted={highlighted}
        $compact={isMobile}
        style={{ zIndex: 2 }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedEmployee(node);
        }}
      >
        <AvatarContainer $compact={isMobile}>
          <Avatar $compact={isMobile}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={fullName} onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }} />
            ) : null}
            <span style={{ display: avatarUrl ? 'none' : 'flex' }}>{initials}</span>
          </Avatar>
        </AvatarContainer>
        <EmployeeName $compact={isMobile}>{fullName || '(Без имени)'}</EmployeeName>
        {node.position && (
          <EmployeePosition $compact={isMobile}>{node.position}</EmployeePosition>
        )}
        {node.department && (
          <EmployeeDepartment $compact={isMobile}>{node.department}</EmployeeDepartment>
        )}
        {directReports > 0 && (
          <div style={{ textAlign: 'center', marginTop: isMobile ? 4 : 8 }}>
            <TeamBadge $compact={isMobile}>
              {directReports} {totalTeam > directReports ? `(${totalTeam})` : ''}
            </TeamBadge>
          </div>
        )}
      </EmployeeCard>
    );
  };

  // Сбор данных о позициях для отрисовки линий на основе реальных позиций карточек
  const collectLineData = (levels) => {
    const lines = [];
    
    if (!chartContentRef.current) return lines;
    
    const containerRect = chartContentRef.current.getBoundingClientRect();
    
    levels.forEach((levelNodes, levelIndex) => {
      if (levelIndex === levels.length - 1) return; // Последний уровень не имеет детей
      
      levelNodes.forEach((node) => {
        if (!node.children || node.children.length === 0) return;
        
        const parentCard = cardRefs.current[node.id];
        if (!parentCard) return;
        
        const parentRect = parentCard.getBoundingClientRect();
        const parentX = parentRect.left - containerRect.left + parentRect.width / 2;
        const parentY = parentRect.top - containerRect.top + parentRect.height;
        
        node.children.forEach(child => {
          const childCard = cardRefs.current[child.id];
          if (!childCard) return;
          
          const childRect = childCard.getBoundingClientRect();
          const childX = childRect.left - containerRect.left + childRect.width / 2;
          const childY = childRect.top - containerRect.top;
          
          const midY = parentY + (childY - parentY) / 2;
          
          lines.push({
            x1: parentX,
            y1: parentY,
            x2: childX,
            y2: childY,
            midY: midY
          });
        });
      });
    });
    
    return lines;
  };

  // Рендеринг уровня
  const renderLevel = (levelNodes, levelIndex) => {
    if (!levelNodes || !Array.isArray(levelNodes) || levelNodes.length === 0) {
      console.warn(`⚠️ Уровень ${levelIndex} пустой или невалидный`);
      return null;
    }
    
    console.log(`🎨 [renderLevel] Рендерим уровень ${levelIndex} с ${levelNodes.length} карточками`);
    
    return (
      <LevelContainer 
        key={`level-${levelIndex}`}
        $isFirst={levelIndex === 0}
        $compact={isMobile}
        style={{
          backgroundColor: levelIndex % 2 === 0 ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.02)',
          padding: isMobile ? '10px 8px' : '20px',
          borderRadius: '12px'
        }}
      >
        <div style={{ 
          textAlign: 'center', 
          marginBottom: isMobile ? '8px' : '16px', 
          color: '#9ca3af', 
          fontSize: isMobile ? '0.65rem' : '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: isMobile ? '0.5px' : '1px'
        }}>
          {isMobile
            ? `Ур. ${levelIndex + 1} · ${levelNodes.length}`
            : `Уровень ${levelIndex + 1} (${levelNodes.length} ${levelNodes.length === 1 ? 'сотрудник' : 'сотрудников'})`}
        </div>
        {levelNodes.map((node) => {
          if (!node || !node.id) {
            console.warn('⚠️ Невалидный узел в уровне:', node);
            return null;
          }
          
          const cardRef = (el) => {
            if (el) {
              cardRefs.current[node.id] = el;
            } else {
              delete cardRefs.current[node.id];
            }
          };
          return renderEmployeeCard(node, levelIndex, cardRef);
        })}
      </LevelContainer>
    );
  };

  // Рендеринг линий
  const renderLines = () => {
    if (lineData.length === 0 || !chartContentRef.current) return null;
    
    const containerRect = chartContentRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    return (
      <LinesOverlay 
        width={containerWidth}
        height={containerHeight}
        style={{ 
          width: `${containerWidth}px`, 
          height: `${containerHeight}px`,
          position: 'absolute',
          top: 0,
          left: 0
        }}
      >
        {lineData.map((line, idx) => (
          <g key={idx}>
            {/* Вертикальная линия от родителя вниз */}
            <line
              x1={line.x1}
              y1={line.y1}
              x2={line.x1}
              y2={line.midY}
              stroke="#6b7280"
              strokeWidth={isMobile ? 1.5 : 2}
            />
            {/* Горизонтальная линия */}
            <line
              x1={Math.min(line.x1, line.x2)}
              y1={line.midY}
              x2={Math.max(line.x1, line.x2)}
              y2={line.midY}
              stroke="#6b7280"
              strokeWidth={isMobile ? 1.5 : 2}
            />
            {/* Вертикальная линия к ребенку */}
            <line
              x1={line.x2}
              y1={line.midY}
              x2={line.x2}
              y2={line.y2}
              stroke="#6b7280"
              strokeWidth={isMobile ? 1.5 : 2}
            />
          </g>
        ))}
      </LinesOverlay>
    );
  };

  // Вычисляем данные для рендеринга
  const filteredTree = filterTree(tree, searchQuery, filterDepartment);
  const isAdmin = state.user?.role === 'admin' || state.user?.role === 'hr';
  const levels = organizeByLevels(filteredTree);

  if (!open) return null;

  return (
    <ModalOverlay $isMobile={isMobile} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader $compact={isMobile}>
          <ModalTitle $compact={isMobile}>
            <FiUsers size={isMobile ? 18 : 22} />
            {isMobile ? 'Структура' : 'Организационная структура'}
          </ModalTitle>
          <CloseBtn $compact={isMobile} onClick={onClose}>
            <FiX />
          </CloseBtn>
        </ModalHeader>
        <ModalBody>
          <Toolbar $compact={isMobile}>
            <SearchInput
              $compact={isMobile}
              type="text"
              placeholder={isMobile ? 'Поиск…' : 'Поиск по имени, должности, департаменту...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <FilterButton
              $compact={isMobile}
              $active={filterDepartment === 'all'}
              onClick={() => setFilterDepartment('all')}
            >
              <FiFilter size={isMobile ? 14 : 16} />
              {isMobile ? 'Все' : 'Все департаменты'}
            </FilterButton>
            {departments.map(dept => (
              <FilterButton
                $compact={isMobile}
                key={dept}
                $active={filterDepartment === dept}
                onClick={() => setFilterDepartment(dept)}
              >
                {dept}
              </FilterButton>
            ))}
            {isAdmin && (
              <>
                <FileInput
                  type="file"
                  id="excel-upload"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                />
                <UploadButton
                  $compact={isMobile}
                  as="label"
                  htmlFor="excel-upload"
                  disabled={uploading}
                >
                  <FiUpload size={isMobile ? 14 : 16} />
                  {uploading ? (isMobile ? '…' : 'Загрузка...') : (isMobile ? 'Excel' : 'Загрузить Excel')}
                </UploadButton>
              </>
            )}
            <ZoomControls $compact={isMobile}>
              <ZoomButton $compact={isMobile} onClick={handleZoomOut} title="Уменьшить">
                <FiZoomOut size={isMobile ? 15 : 18} />
              </ZoomButton>
              <span style={{ fontSize: isMobile ? '0.72rem' : '0.85rem', color: '#6b7280', minWidth: isMobile ? '36px' : '50px', textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </span>
              <ZoomButton $compact={isMobile} onClick={handleZoomIn} title="Увеличить">
                <FiZoomIn size={isMobile ? 15 : 18} />
              </ZoomButton>
              <ZoomButton $compact={isMobile} onClick={handleResetZoom} title="Сбросить">
                <FiMaximize2 size={isMobile ? 15 : 18} />
              </ZoomButton>
            </ZoomControls>
          </Toolbar>
          
          <ChartContainer>
            {loading ? (
              <LoadingState $compact={isMobile}>
                <p>Загрузка структуры компании...</p>
              </LoadingState>
            ) : filteredTree.length === 0 ? (
              <EmptyState $compact={isMobile}>
                <FiUsers />
                <h3>Структура компании пуста</h3>
                <p>
                  {isAdmin 
                    ? (isMobile ? 'Загрузите Excel со структурой.' : 'Загрузите Excel файл со структурой компании для начала работы')
                    : (isMobile ? 'Структура ещё не загружена.' : 'Структура компании еще не загружена')}
                </p>
              </EmptyState>
            ) : (
              <ChartWrapper
                ref={chartWrapperRef}
                $isDragging={isDragging}
                onMouseDown={handleMouseDown}
              >
                <ChartContent
                  ref={chartContentRef}
                  $compact={isMobile}
                  $scale={scale}
                  $translateX={translateX}
                  $translateY={translateY}
                  $isDragging={isDragging}
                >
                  {renderLines()}
                  {levels.length > 0 ? (
                    levels.map((levelNodes, levelIndex) => {
                      console.log(`🎯 Рендеринг уровень ${levelIndex} с ${levelNodes.length} карточками`);
                      return renderLevel(levelNodes, levelIndex);
                    })
                  ) : (
                    <div style={{ padding: isMobile ? '20px 12px' : '40px', textAlign: 'center', color: '#6b7280', fontSize: isMobile ? '0.85rem' : 'inherit' }}>
                      Нет данных для отображения
                    </div>
                  )}
                </ChartContent>
              </ChartWrapper>
            )}
          </ChartContainer>
        </ModalBody>
      </ModalContainer>

      {/* Модалка с информацией о сотруднике */}
      {selectedEmployee && (
        <EmployeeDetailsOverlay $compact={isMobile} onClick={() => setSelectedEmployee(null)}>
          <EmployeeDetailsContainer $compact={isMobile} onClick={(e) => e.stopPropagation()}>
            <EmployeeDetailsHeader $compact={isMobile}>
              <EmployeeDetailsTitle $compact={isMobile}>
                {isMobile ? 'Сотрудник' : 'Информация о сотруднике'}
              </EmployeeDetailsTitle>
              <CloseBtn $compact={isMobile} onClick={() => setSelectedEmployee(null)}>
                <FiX />
              </CloseBtn>
            </EmployeeDetailsHeader>
            <EmployeeDetailsBody $compact={isMobile}>
              <EmployeeDetailsAvatarSection $compact={isMobile}>
                <EmployeeDetailsAvatar $compact={isMobile}>
                  {findUserAvatar(selectedEmployee.first_name, selectedEmployee.last_name) ? (
                    <img 
                      src={findUserAvatar(selectedEmployee.first_name, selectedEmployee.last_name)} 
                      alt={`${selectedEmployee.first_name} ${selectedEmployee.last_name}`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span style={{ display: findUserAvatar(selectedEmployee.first_name, selectedEmployee.last_name) ? 'none' : 'flex' }}>
                    {getInitials(selectedEmployee.first_name, selectedEmployee.last_name)}
                  </span>
                </EmployeeDetailsAvatar>
                <EmployeeDetailsFullName $compact={isMobile}>
                  {`${selectedEmployee.first_name || ''} ${selectedEmployee.last_name || ''}`.trim() || 'Не указано'}
                </EmployeeDetailsFullName>
              </EmployeeDetailsAvatarSection>

              <EmployeeDetailsInfo $compact={isMobile}>
                {selectedEmployee.position && (
                  <InfoRow>
                    <InfoLabel>Должность:</InfoLabel>
                    <InfoValue>{selectedEmployee.position}</InfoValue>
                  </InfoRow>
                )}
                
                {selectedEmployee.department && (
                  <InfoRow>
                    <InfoLabel>Департамент:</InfoLabel>
                    <InfoValue>{selectedEmployee.department}</InfoValue>
                  </InfoRow>
                )}
                
                {selectedEmployee.subdivision && (
                  <InfoRow>
                    <InfoLabel>Подразделение:</InfoLabel>
                    <InfoValue>{selectedEmployee.subdivision}</InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.location && (
                  <InfoRow>
                    <InfoLabel>Локация:</InfoLabel>
                    <InfoValue>{selectedEmployee.location}</InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.employment_type && (
                  <InfoRow>
                    <InfoLabel>Вид занятости:</InfoLabel>
                    <InfoValue>{selectedEmployee.employment_type}</InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.start_date && (
                  <InfoRow>
                    <InfoLabel>Дата начала:</InfoLabel>
                    <InfoValue>
                      {new Date(selectedEmployee.start_date).toLocaleDateString('uk-UA', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.end_date && (
                  <InfoRow>
                    <InfoLabel>Исп. срок заканчивается:</InfoLabel>
                    <InfoValue>
                      {new Date(selectedEmployee.end_date).toLocaleDateString('uk-UA', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.termination_date && (
                  <InfoRow>
                    <InfoLabel>Исп. срок заканчивается:</InfoLabel>
                    <InfoValue>
                      {new Date(selectedEmployee.termination_date).toLocaleDateString('uk-UA', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.email && (
                  <InfoRow>
                    <InfoLabel>Email:</InfoLabel>
                    <InfoValue>{selectedEmployee.email}</InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.phone && (
                  <InfoRow>
                    <InfoLabel>Телефон:</InfoLabel>
                    <InfoValue>{selectedEmployee.phone}</InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.manager_name && (
                  <InfoRow>
                    <InfoLabel>Менеджер:</InfoLabel>
                    <InfoValue>{selectedEmployee.manager_name}</InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.children && selectedEmployee.children.length > 0 && (
                  <InfoRow>
                    <InfoLabel>Прямые подчиненные:</InfoLabel>
                    <InfoValue>
                      {selectedEmployee.children.length} {selectedEmployee.children.length === 1 ? 'сотрудник' : 'сотрудников'}
                    </InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.children && selectedEmployee.children.length > 0 && countAllSubordinates(selectedEmployee) > selectedEmployee.children.length && (
                  <InfoRow>
                    <InfoLabel>Всего в команде:</InfoLabel>
                    <InfoValue>{countAllSubordinates(selectedEmployee)} сотрудников</InfoValue>
                  </InfoRow>
                )}

                {selectedEmployee.children && selectedEmployee.children.length > 0 && (
                  <InfoRow>
                    <InfoLabel style={{ marginBottom: '8px' }}>Команда:</InfoLabel>
                    <TeamList $compact={isMobile}>
                      {selectedEmployee.children.map(child => (
                        <TeamMember $compact={isMobile} key={child.id}>
                          <div>
                            {findUserAvatar(child.first_name, child.last_name) ? (
                              <img 
                                src={findUserAvatar(child.first_name, child.last_name)} 
                                alt={`${child.first_name} ${child.last_name}`}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <span style={{ display: findUserAvatar(child.first_name, child.last_name) ? 'none' : 'flex' }}>
                              {getInitials(child.first_name, child.last_name)}
                            </span>
                          </div>
                          <span style={{ marginLeft: isMobile ? 8 : 12, fontSize: isMobile ? '0.8rem' : 'inherit', lineHeight: 1.3 }}>
                            {`${child.first_name || ''} ${child.last_name || ''}`.trim()}
                            {child.position && ` - ${child.position}`}
                          </span>
                        </TeamMember>
                      ))}
                    </TeamList>
                  </InfoRow>
                )}
              </EmployeeDetailsInfo>
            </EmployeeDetailsBody>
          </EmployeeDetailsContainer>
        </EmployeeDetailsOverlay>
      )}
    </ModalOverlay>
  );
}
